import {
  chatBellSharedState,
  updateChatBellHead,
} from "@/src/components/Notification/chatBellSharedState";
import {
  hideNotifyAction,
  markNotifyMessageAsRead,
  setLatestSeen,
} from "@/src/components/Notification/localNotificationState";
import {
  isNotificationSyncDebugEnabled,
  logNotificationSync,
} from "./notificationSyncTelemetry";

import { emitNotificationUserStateUpdated } from "./notificationSyncSignal";
import { getSupabaseBrowserClient } from "./supabaseBrowserClient";
import { upsertTrackerReadWatermark } from "@/src/features/tracker/trackerReadWatermark";
import { deviceDetector } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type {
  REALTIME_SUBSCRIBE_STATES,
  RealtimePostgresChangesPayload,
} from "@supabase/supabase-js";
import { shouldUseSupabaseNotificationSync } from "./notificationSyncBackend";

type NotificationPrefsRow = {
  owner_id: string;
  latest_seen_id: string | null;
  latest_seen_time: number | null;
};

type NotificationReadRow = {
  owner_id: string;
  message_id: string;
  read_at: number;
};

type NotificationHiddenRow = {
  owner_id: string;
  message_id: string;
  cat_id: string;
  hidden_at: number;
};

type NotificationTrackerReadRow = {
  owner_id: string;
  tracker_id: string;
  read_at: number;
};

let activeOwnerId: string | null = null;
let activeChannel: ReturnType<
  ReturnType<typeof getSupabaseBrowserClient>["channel"]
> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let snapshotPollingTimer: ReturnType<typeof setInterval> | null = null;

const MAX_REALTIME_RECONNECT_ATTEMPTS = 6;
const REALTIME_RECONNECT_BASE_DELAY_MS = 2_000;
const SNAPSHOT_POLLING_INTERVAL_MS = 15_000;

function channelName(ownerId: string): string {
  return `notification-user-state-${ownerId}`;
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function stopSnapshotPolling(): void {
  if (snapshotPollingTimer) {
    clearInterval(snapshotPollingTimer);
    snapshotPollingTimer = null;
  }
}

function ensureSnapshotPolling(ownerId: string): void {
  if (snapshotPollingTimer) {
    return;
  }
  snapshotPollingTimer = setInterval(() => {
    if (activeOwnerId !== ownerId) {
      return;
    }
    void applyInitialSnapshot(ownerId);
  }, SNAPSHOT_POLLING_INTERVAL_MS);
  logNotificationSync("realtime_event", {
    ownerId,
    table: "channel",
    event: "snapshot_polling_start",
    status: "ok",
    extra: { intervalMs: SNAPSHOT_POLLING_INTERVAL_MS },
  });
}

async function applyInitialSnapshot(ownerId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();

  const [
    { data: prefs },
    { data: reads },
    { data: hides },
    { data: trackerReads },
  ] = await Promise.all([
    supabase
      .from("user_notification_prefs")
      .select("owner_id,latest_seen_id,latest_seen_time")
      .eq("owner_id", ownerId)
      .maybeSingle<NotificationPrefsRow>(),
    supabase
      .from("user_notification_message_reads")
      .select("owner_id,message_id,read_at")
      .eq("owner_id", ownerId)
      .returns<NotificationReadRow[]>(),
    supabase
      .from("user_notification_action_hides")
      .select("owner_id,message_id,cat_id,hidden_at")
      .eq("owner_id", ownerId)
      .returns<NotificationHiddenRow[]>(),
    supabase
      .from("user_tracker_reads")
      .select("owner_id,tracker_id,read_at")
      .eq("owner_id", ownerId)
      .returns<NotificationTrackerReadRow[]>(),
  ]);

  const latestSeenTime = toFiniteTimestamp(prefs?.latest_seen_time);
  if (prefs?.latest_seen_id && latestSeenTime !== null) {
    setLatestSeen(
      ownerId,
      { id: prefs.latest_seen_id, time: latestSeenTime },
      { syncToSupabase: false },
    );
    if (
      chatBellSharedState.ownerId === ownerId &&
      chatBellSharedState.latestHead?.id
    ) {
      updateChatBellHead(ownerId, chatBellSharedState.latestHead);
    }
  }

  (reads ?? []).forEach((row: NotificationReadRow) => {
    const readAt = toFiniteTimestamp(row.read_at);
    if (readAt === null) {
      return;
    }
    markNotifyMessageAsRead(ownerId, row.message_id, readAt, {
      syncToSupabase: false,
    });
  });

  (hides ?? []).forEach((row: NotificationHiddenRow) => {
    const hiddenAt = toFiniteTimestamp(row.hidden_at);
    if (hiddenAt === null) {
      return;
    }
    hideNotifyAction(ownerId, row.message_id, row.cat_id, hiddenAt, {
      syncToSupabase: false,
    });
  });

  (trackerReads ?? []).forEach((row: NotificationTrackerReadRow) => {
    const readAt = toFiniteTimestamp(row.read_at);
    if (readAt === null) {
      return;
    }
    upsertTrackerReadWatermark(
      ownerId,
      row.tracker_id,
      readAt,
      "realtime_sync",
    );
  });

  emitNotificationUserStateUpdated();
  logNotificationSync("pull_snapshot", {
    ownerId,
    status: "ok",
    extra: {
      hasPrefs: !!prefs,
      readCount: reads?.length ?? 0,
      hideCount: hides?.length ?? 0,
      trackerReadCount: trackerReads?.length ?? 0,
    },
  });
}

function pickLagMs(value?: number | null): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  return Math.max(Date.now() - value, 0);
}

function toFiniteTimestamp(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function extractOwnerId(value: unknown): string | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  if (!("owner_id" in value)) {
    return undefined;
  }
  const ownerId = (value as { owner_id?: unknown }).owner_id;
  return typeof ownerId === "string" ? ownerId : undefined;
}

function handlePrefsChange(ownerId: string, row: NotificationPrefsRow): void {
  const latestSeenTime = toFiniteTimestamp(row.latest_seen_time);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][prefs-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      latestSeenId: row.latest_seen_id,
      latestSeenTimeRaw: row.latest_seen_time,
      latestSeenTimeType: typeof row.latest_seen_time,
      latestSeenTimeParsed: latestSeenTime,
    });
    log.info("[sync-debug][prefs-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      latestSeenId: row.latest_seen_id,
      latestSeenTimeRaw: row.latest_seen_time,
      latestSeenTimeType: typeof row.latest_seen_time,
      latestSeenTimeParsed: latestSeenTime,
    });
  }
  if (!row.latest_seen_id || latestSeenTime === null) {
    return;
  }
  setLatestSeen(
    ownerId,
    { id: row.latest_seen_id, time: latestSeenTime },
    { syncToSupabase: false },
  );
  if (
    chatBellSharedState.ownerId === ownerId &&
    chatBellSharedState.latestHead
  ) {
    updateChatBellHead(ownerId, chatBellSharedState.latestHead);
  }
  emitNotificationUserStateUpdated();
  logNotificationSync("realtime_event", {
    ownerId,
    table: "user_notification_prefs",
    event: "change",
    status: "ok",
    lagMs: pickLagMs(latestSeenTime),
  });
}

function handleReadChange(ownerId: string, row: NotificationReadRow): void {
  const readAt = toFiniteTimestamp(row.read_at);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][read-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      messageId: row.message_id,
      readAtRaw: row.read_at,
      readAtType: typeof row.read_at,
      readAtParsed: readAt,
    });
    log.info("[sync-debug][read-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      messageId: row.message_id,
      readAtRaw: row.read_at,
      readAtType: typeof row.read_at,
      readAtParsed: readAt,
    });
  }
  if (readAt === null) {
    return;
  }
  markNotifyMessageAsRead(ownerId, row.message_id, readAt, {
    syncToSupabase: false,
  });
  emitNotificationUserStateUpdated();
  logNotificationSync("realtime_event", {
    ownerId,
    table: "user_notification_message_reads",
    event: "change",
    status: "ok",
    lagMs: pickLagMs(readAt),
  });
}

function handleHideChange(ownerId: string, row: NotificationHiddenRow): void {
  const hiddenAt = toFiniteTimestamp(row.hidden_at);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][hide-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      messageId: row.message_id,
      catId: row.cat_id,
      hiddenAtRaw: row.hidden_at,
      hiddenAtType: typeof row.hidden_at,
      hiddenAtParsed: hiddenAt,
    });
    log.info("[sync-debug][hide-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      messageId: row.message_id,
      catId: row.cat_id,
      hiddenAtRaw: row.hidden_at,
      hiddenAtType: typeof row.hidden_at,
      hiddenAtParsed: hiddenAt,
    });
  }
  if (hiddenAt === null) {
    return;
  }
  hideNotifyAction(ownerId, row.message_id, row.cat_id, hiddenAt, {
    syncToSupabase: false,
  });
  emitNotificationUserStateUpdated();
  logNotificationSync("realtime_event", {
    ownerId,
    table: "user_notification_action_hides",
    event: "change",
    status: "ok",
    lagMs: pickLagMs(hiddenAt),
  });
}

function handleTrackerReadChange(
  ownerId: string,
  row: NotificationTrackerReadRow,
): void {
  const readAt = toFiniteTimestamp(row.read_at);
  if (isNotificationSyncDebugEnabled()) {
    log.info("[sync-debug][tracker-read-change]", {
      activeOwnerId,
      subscribedOwnerId: ownerId,
      rowOwnerId: row.owner_id,
      trackerId: row.tracker_id,
      readAtRaw: row.read_at,
      readAtType: typeof row.read_at,
      readAtParsed: readAt,
    });
  }
  if (readAt === null) {
    return;
  }
  upsertTrackerReadWatermark(ownerId, row.tracker_id, readAt, "realtime_sync");
  const device = deviceDetector.isIOSApp()
    ? "ios"
    : deviceDetector.isAndroidApp()
      ? "android"
      : "web";
  log.trace("[GTM_VIEW][signal_read_cross_device]", {
    signal_id: row.tracker_id,
    synced_from_source: "realtime_sync",
    device,
  });
  logNotificationSync("realtime_event", {
    ownerId,
    table: "user_tracker_reads",
    event: "change",
    status: "ok",
    lagMs: pickLagMs(readAt),
  });
}

function shouldRetryRealtime(status: REALTIME_SUBSCRIBE_STATES): boolean {
  return (
    status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED"
  );
}

async function attachRealtimeChannel(ownerId: string): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  activeChannel = supabase
    .channel(channelName(ownerId))
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_notification_prefs",
        filter: `owner_id=eq.${ownerId}`,
      },
      (payload: RealtimePostgresChangesPayload<NotificationPrefsRow>) => {
        if (isNotificationSyncDebugEnabled()) {
          log.info("[sync-debug][realtime-payload]", {
            ownerId,
            table: "user_notification_prefs",
            eventType: payload.eventType,
            hasNew: !!payload.new,
            hasOld: !!payload.old,
            rowOwnerId:
              extractOwnerId(payload.new) ?? extractOwnerId(payload.old),
          });
        }
        const row = (payload.new ?? payload.old) as
          | NotificationPrefsRow
          | undefined;
        if (!row) {
          return;
        }
        handlePrefsChange(ownerId, row);
      },
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_notification_message_reads",
        filter: `owner_id=eq.${ownerId}`,
      },
      (payload: RealtimePostgresChangesPayload<NotificationReadRow>) => {
        if (isNotificationSyncDebugEnabled()) {
          log.error("[sync-debug][realtime-payload]", {
            ownerId,
            table: "user_notification_message_reads",
            eventType: payload.eventType,
            hasNew: !!payload.new,
            hasOld: !!payload.old,
            rowOwnerId:
              extractOwnerId(payload.new) ?? extractOwnerId(payload.old),
          });
        }
        const row = (payload.new ?? payload.old) as
          | NotificationReadRow
          | undefined;
        if (!row) {
          return;
        }
        handleReadChange(ownerId, row);
      },
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_notification_action_hides",
        filter: `owner_id=eq.${ownerId}`,
      },
      (payload: RealtimePostgresChangesPayload<NotificationHiddenRow>) => {
        if (isNotificationSyncDebugEnabled()) {
          log.info("[sync-debug][realtime-payload]", {
            ownerId,
            table: "user_notification_action_hides",
            eventType: payload.eventType,
            hasNew: !!payload.new,
            hasOld: !!payload.old,
            rowOwnerId:
              extractOwnerId(payload.new) ?? extractOwnerId(payload.old),
          });
        }
        const row = (payload.new ?? payload.old) as
          | NotificationHiddenRow
          | undefined;
        if (!row) {
          return;
        }
        handleHideChange(ownerId, row);
      },
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "user_tracker_reads",
        filter: `owner_id=eq.${ownerId}`,
      },
      (payload: RealtimePostgresChangesPayload<NotificationTrackerReadRow>) => {
        if (isNotificationSyncDebugEnabled()) {
          log.info("[sync-debug][realtime-payload]", {
            ownerId,
            table: "user_tracker_reads",
            eventType: payload.eventType,
            hasNew: !!payload.new,
            hasOld: !!payload.old,
            rowOwnerId:
              extractOwnerId(payload.new) ?? extractOwnerId(payload.old),
          });
        }
        const row = (payload.new ?? payload.old) as
          | NotificationTrackerReadRow
          | undefined;
        if (!row) {
          return;
        }
        handleTrackerReadChange(ownerId, row);
      },
    )
    .subscribe((status: REALTIME_SUBSCRIBE_STATES) => {
      log.info("[notification][supabase] realtime status", {
        ownerId,
        status,
      });
      logNotificationSync("realtime_event", {
        ownerId,
        table: "channel",
        event: "subscribe",
        status,
      });

      if (status === "SUBSCRIBED") {
        reconnectAttempt = 0;
        clearReconnectTimer();
        stopSnapshotPolling();
        return;
      }

      if (!shouldRetryRealtime(status) || activeOwnerId !== ownerId) {
        return;
      }

      // Keep eventual consistency when realtime channel is unstable.
      ensureSnapshotPolling(ownerId);

      if (reconnectAttempt >= MAX_REALTIME_RECONNECT_ATTEMPTS) {
        logNotificationSync("realtime_event", {
          ownerId,
          table: "channel",
          event: "reconnect_give_up",
          status: "max_retries",
        });
        return;
      }

      clearReconnectTimer();
      const delay = Math.min(
        REALTIME_RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt,
        30_000,
      );
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => {
        if (activeOwnerId !== ownerId) {
          return;
        }
        void (async () => {
          try {
            const currentChannel = activeChannel;
            if (currentChannel) {
              const client = getSupabaseBrowserClient();
              await client.removeChannel(currentChannel);
            }
          } catch {
            // Best-effort cleanup before reconnect.
          }
          if (activeOwnerId === ownerId) {
            await attachRealtimeChannel(ownerId);
          }
        })();
      }, delay);
    });
}

export async function startNotificationUserStateSync(
  ownerId: string,
): Promise<void> {
  if (!shouldUseSupabaseNotificationSync()) {
    return;
  }
  if (!ownerId) {
    return;
  }
  if (activeOwnerId === ownerId) {
    return;
  }

  await stopNotificationUserStateSync();
  activeOwnerId = ownerId;
  reconnectAttempt = 0;
  clearReconnectTimer();
  stopSnapshotPolling();

  await applyInitialSnapshot(ownerId);
  await attachRealtimeChannel(ownerId);
}

export async function refreshNotificationUserStateSnapshot(
  ownerId: string,
): Promise<void> {
  if (!shouldUseSupabaseNotificationSync()) {
    return;
  }
  if (!ownerId) {
    return;
  }
  await applyInitialSnapshot(ownerId);
}

/** 原生壳 Tab freeze：暂停 15s snapshot 轮询，Realtime channel 保持连接。 */
export function pauseNotificationSnapshotPollingForNativeTabFreeze(): void {
  stopSnapshotPolling();
}

/** 原生壳 Tab resume：若 sync 仍在运行则恢复 snapshot 轮询。 */
export function resumeNotificationSnapshotPollingForNativeTabFreeze(): void {
  if (activeOwnerId) {
    ensureSnapshotPolling(activeOwnerId);
  }
}

export async function stopNotificationUserStateSync(): Promise<void> {
  clearReconnectTimer();
  reconnectAttempt = 0;
  stopSnapshotPolling();
  try {
    if (!shouldUseSupabaseNotificationSync()) {
      activeOwnerId = null;
      activeChannel = null;
      return;
    }
    const supabase = getSupabaseBrowserClient();
    if (activeChannel) {
      await supabase.removeChannel(activeChannel);
    }
  } catch {
    // Ignore teardown errors when supabase env is absent.
  }
  activeOwnerId = null;
  activeChannel = null;
}
